import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { omitKeys } from '@adsk/alloy-react-helpers';
import { StylableRenderPropType } from '@adsk/alloy-react-theme';

import {
  NUMERIC_INPUT_TYPES,
  SIGN_DISPLAY_TYPES,
  TRAILING_ZERO_DISPLAY_TYPES,
} from '../consts';
import NumericInput from './NumericInput';

type CurrencyInputProps = Omit<
  React.ComponentProps<typeof NumericInput>,
  'minimumFractionDigits' | 'maximumFractionDigits' | 'maximumSignificantDigits'
> & {
  currency: string;
};

const defaultRenderLeftElement = ({
  symbol,
  ...props
}: StylableRenderPropType & { symbol: string }) => {
  if (!symbol) return null;
  return (
    <div data-testid="currency-symbol" {...props}>
      {symbol}
    </div>
  );
};

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      currency,
      locale,
      useGrouping = true,
      renderLeftElement = defaultRenderLeftElement,
      renderRightElement,
      ...props
    },
    ref,
  ) => {
    const [symbol, numDecimals] = useMemo(() => {
      // Extract the currency symbol in the specified locale.
      let formatter;
      try {
        formatter = Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          useGrouping: false,
          numberingSystem: 'latn',
        });
      } catch (e) {
        console.warn(e);
        return ['', 2];
      }

      const parts = formatter.formatToParts(0);
      const symbol =
        parts.find((part) => part.type === 'currency')?.value ?? '';
      const numDecimals =
        parts.find((part) => part.type === 'fraction')?.value.length ?? 0;
      return [symbol, numDecimals];
    }, [currency, locale]);

    return (
      <NumericInput
        {...props}
        locale={locale}
        minimumFractionDigits={numDecimals}
        maximumFractionDigits={numDecimals}
        useGrouping={useGrouping}
        ref={ref}
        renderLeftElement={(p: StylableRenderPropType) =>
          renderLeftElement({ ...p, symbol })
        }
        renderRightElement={
          renderRightElement
            ? (p: StylableRenderPropType) =>
                renderRightElement({ ...p, symbol })
            : undefined
        }
      />
    );
  },
);

CurrencyInput.displayName = 'CurrencyInput';

CurrencyInput.propTypes = {
  ...omitKeys(CurrencyInput.propTypes, [
    'minimumFractionDigits',
    'maximumFractionDigits',
    'maximumSignificantDigits',
  ]),
  /** The currency's ISO 4217 code (e.g. USD) */
  currency: PropTypes.string.isRequired,
  /** Locale to use for formatting the currency and number. Defaults to the browser's configured locales */
  locale: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

export default Object.assign(CurrencyInput, {
  TYPES: NUMERIC_INPUT_TYPES,
  SIGN_DISPLAY_TYPES,
  TRAILING_ZERO_DISPLAY_TYPES,
});
